import React, { useState, useRef, useCallback, FC } from 'react';
import {
  View,
  Text,
  Animated,
  NativeScrollEvent,
  NativeSyntheticEvent,
  StyleProp,
  ViewStyle,
  ScrollView,
  StyleSheet,
} from 'react-native';
import { connect, useSelector } from 'react-redux';
import { useFocusEffect } from '@react-navigation/native';

import Loading from '@components/loading';

import { notchTop, scale } from '@components/frame';
import {
  controlAPICall,
  formatStartOf,
  isPhone,
  isPhoneNative,
  isWeb,
  localTime,
  machineDetailsConditions,
  ncFileStatuses,
} from '../../../../../../../helpers';

import useStatusBar from '@components/common/useStatusBar';

import { font } from '@styles/fonts';
import ColorWrapper from '@styles/colors';

import { switchMachine, getMachines, setSelectedMachine, getNCFileStatistics } from '@reducers/machines';
import { removeProductionInformation, setSelectedNCFile } from '@reducers/manufacture';
import { MachinesScreenContentWrapperProps } from '@v2/pages/machines/common/types/props';
import MachinesScreenContentRenderer from './components/MachinesScreenContentRenderer/MachinesScreenContentRenderer';
import { MachinesScreenHeader } from './components/MachinesScreenHeader/MachinesScreenHeader';
import { MachinesScreenMobileHeader } from '@v2/pages/machines/common/components/MachinesScreenMobileHeader/MachinesScreenMobileHeader';
import { useMachinesContext } from '@v2/pages/machines/common/providers/MachinesProvider/useMachinesContext';
import { useHandleNavigationMachinesHeader } from '@v2/pages/machines/hooks/useHandleNavigationMachineHeader';
import DetailsComponents from '@components/machines/components/detailsComponents';
import { MachinesScreen } from '@v2/pages/machines/common/enums';
import { DATE_FORMAT } from '@v2/pages/common/constants/base.constants';
import { setActionsModalOpen, setAllowLoadMoreData } from '@reducers/machinesV2';
import { selectCurrentScreenTitle } from '@reducers/header';
import { useHandleParams } from '@v2/pages/machines/hooks/useHandleParams';
import { usePanelViewMode } from '@v2/pages/machines/hooks/usePanelViewMode';
import { useDeviceSize } from '@v2/pages/common/hooks/useDeviceSize';
import { useBackgroundColor } from '@v2/pages/machines/hooks/useBackgroundColor';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import TouchableElement from '@components/common/touchableElement';

const MachinesScreenContentWrapperComponent: FC<MachinesScreenContentWrapperProps> = (props) => {
  const {
    C,
    time,
    machinesLoaded,
    children,
    globalScroll,
    withDefaultComponent,
    withoutHeader,
    selectedChartDate,
    initialLoading,
    allowLoadMoreData,
    setAllowLoadMoreData,
    loadDataDisabled,
    displayScrollViewLoader,
    selectedItem,
    ncFileStatistics,
  } = props;

  const { lg } = useMachinesContext();
  const { panelViewMode } = usePanelViewMode();
  const { detailsWidth } = useDeviceSize();
  const insets = useSafeAreaInsets();

  const {
    isNowSubScreen,
    shouldUseGlobalScroll,
    activeScreenNameWithoutMachineMainScreen,
    shouldDisplayGlobalScrollLoader,
    enableWholePageVerticalScroll,
  } = useHandleNavigationMachinesHeader();
  const { isAllSelected: allSelected, timePeriod: selectedTime } = useHandleParams();

  const [localSelectedNCFile, setLocalSelectedNCFile] = useState<Record<string, unknown> | null>(null);

  const [alowLoadMore, setLoadMore] = useState(true);

  const abortControllerNCFile = useRef();

  useFocusEffect(
    useCallback(() => {
      return () => {
        abortControllerNCFile.current?.abort();
      };
    }, [])
  );

  if (localSelectedNCFile) {
    setSelectedMachine({
      ...selectedItem,
      machineStatus: {
        ...selectedItem.machineStatus,
        ...localSelectedNCFile,
      },
    });
  }

  const isNCFile = Boolean(selectedItem?.machineStatus?.ncFile);

  const s = scale;

  useStatusBar({
    C,
    background: C('Background/Light/Detail'),
  });

  const fadeAnim = useRef(new Animated.Value(0)).current;
  const scrollY = useRef(new Animated.Value(0)).current;
  const scrollViewRef = useRef<ScrollView>(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [isOverlayInteractive, setIsOverlayInteractive] = useState(false);
  const isOverlayInteractiveRef = useRef(false);
  const currentScreenTitle = useSelector(selectCurrentScreenTitle);

  const handleScrollToTop = () => {
    scrollViewRef.current?.scrollTo({ y: 0, animated: true });
  };

  // Animation interpolations for title
  const titleOpacity = scrollY.interpolate({
    inputRange: [0, headerHeight * 0.5, headerHeight],
    outputRange: [0, 0, 1],
    extrapolate: 'clamp',
  });

  const {
    server: { offset = 0 },
  } = time;

  const { machineScreenMainBg } = useBackgroundColor();

  const backgroundColor = C(machineScreenMainBg);

  const scrollListener = ({ nativeEvent }: NativeSyntheticEvent<NativeScrollEvent>) => {
    const loadMoreNCFiles =
      !isNowSubScreen && [MachinesScreen.ACTIVE, MachinesScreen.PRODUCTIVE].some((s) => activeScreenNameWithoutMachineMainScreen.includes(s));

    const isCloseToBottom = ({ layoutMeasurement, contentOffset, contentSize }) => {
      return layoutMeasurement.height + contentOffset.y >= contentSize.height - 500;
    };

    if (isCloseToBottom(nativeEvent) && !allowLoadMoreData && !loadDataDisabled) {
      setAllowLoadMoreData(true);
      setTimeout(() => setAllowLoadMoreData(false), 1000);
    }

    if (loadMoreNCFiles && isCloseToBottom(nativeEvent) && alowLoadMore) {
      setLoadMore(false);
      const amount = ncFileStatistics?.data?.length;
      if (amount && ncFileStatistics.isMore) {
        const momentStart = selectedChartDate ? localTime(selectedChartDate) : localTime();

        const d = { period: selectedTime, date: momentStart.startOf(formatStartOf(selectedTime)).format(DATE_FORMAT) };

        if (allSelected) {
          delete d.machineId;
        }
        controlAPICall(getNCFileStatistics, d, abortControllerNCFile);
      }
      setTimeout(() => {
        setLoadMore(true);
      }, 1000);
    }
  };

  const { status, ncFileStatus, subStatus, ncFile } = selectedItem?.machineStatus || {};
  const { machineActive } = machineDetailsConditions({ status, ncFileStatus, subStatus, ncFileExist: !!ncFile });
  const showMachineInfo = (ncFileStatus ? ncFileStatuses.Linked === ncFileStatus : true) ? true : machineActive;

  const Content =
    withDefaultComponent && !allSelected ? (
      <DetailsComponents
        {...{
          panelViewMode,
          selectedItem,
          C,
          timeOffset: offset,
          showMachineInfo,
          font,
          scale: s,
          noMachine: false,
          isNCFile,
          lg,
          setLocalSelectedNCFile,
          machinesLoaded,
          initialLoading,
          detailsWidth,
          setSelectedMachine,
        }}
      >
        {children}
      </DetailsComponents>
    ) : (
      children
    );

  const mainContainerStyles: StyleProp<ViewStyle> = [
    {
      flex: 1,
      width: isPhone ? '100%' : detailsWidth,
    },
    {
      backgroundColor,
    },
  ];

  const content = (
    <>
      <View
        onLayout={(event) => {
          const { height } = event.nativeEvent.layout;
          setHeaderHeight(height);
        }}
      >
        {!withoutHeader && !isPhone && <MachinesScreenHeader panelViewMode={panelViewMode} />}
        {!withoutHeader && isPhone && <MachinesScreenMobileHeader />}
      </View>
      <View
        style={[
          isPhone
            ? { flex: 1 }
            : {
                width: '100%',
                flex: 1,
              },
        ]}
      >
        <MachinesScreenContentRenderer C={C}>
          {!shouldUseGlobalScroll ? (
            <View style={[isPhone ? { flex: 1 } : { width: '100%', flex: 1 }]}>{Content}</View>
          ) : (
            <Animated.ScrollView
              ref={globalScroll}
              onScroll={Animated.event([{ nativeEvent: { contentOffset: { y: fadeAnim } } }], { listener: scrollListener, useNativeDriver: false })}
              showsVerticalScrollIndicator={false}
              contentContainerStyle={[
                {
                  backgroundColor,
                },
                { minHeight: '100%' },
              ]}
            >
              <View style={[isPhone ? { flex: 1 } : { width: '100%', flex: 1 }]}>{Content}</View>
            </Animated.ScrollView>
          )}

          {displayScrollViewLoader && shouldDisplayGlobalScrollLoader && (
            <View style={{ position: 'absolute', bottom: scale(20), left: '50%', transform: [{ translateX: '-50%' }] }}>
              <Loading background={'transparent'} />
            </View>
          )}
        </MachinesScreenContentRenderer>
      </View>
    </>
  );

  if (enableWholePageVerticalScroll) {
    return (
      <View style={mainContainerStyles}>
        <Animated.ScrollView
          ref={scrollViewRef}
          onScroll={Animated.event([{ nativeEvent: { contentOffset: { y: scrollY } } }], {
            useNativeDriver: false,
            listener: (event: NativeSyntheticEvent<NativeScrollEvent>) => {
              const offsetY = event.nativeEvent.contentOffset.y;
              const shouldBeInteractive = headerHeight > 0 && offsetY >= headerHeight;
              if (shouldBeInteractive !== isOverlayInteractiveRef.current) {
                isOverlayInteractiveRef.current = shouldBeInteractive;
                setIsOverlayInteractive(shouldBeInteractive);
              }
            },
          })}
          scrollEventThrottle={16}
          showsVerticalScrollIndicator={false}
        >
          {content}
        </Animated.ScrollView>
        {currentScreenTitle && (
          <View pointerEvents="box-none" style={StyleSheet.absoluteFill}>
            <TouchableElement onPress={handleScrollToTop}>
              {() => (
                <Animated.View
                  pointerEvents={isOverlayInteractive ? 'auto' : 'none'}
                  style={{
                    position: 'absolute',
                    height: isPhoneNative ? scale(55) + notchTop : !isWeb ? scale(73) + insets.top : scale(82),
                    width: '100%',
                    flex: 1,
                    flexDirection: 'row',
                    justifyContent: 'center',
                    backgroundColor: C('Background/Light/Header App'),
                    borderBottomColor: C('Hairline/Light/No trans'),
                    borderBottomWidth: StyleSheet.hairlineWidth,
                    paddingTop: isPhoneNative ? scale(23) : scale(34),
                    paddingBottom: isPhoneNative ? scale(8) : scale(12),
                    opacity: titleOpacity,
                  }}
                >
                  <Text
                    style={[
                      font.Headline,
                      {
                        color: C('Label/Light/Primary'),
                        alignSelf: 'flex-end',
                      },
                    ]}
                  >
                    {currentScreenTitle}
                  </Text>
                </Animated.View>
              )}
            </TouchableElement>
          </View>
        )}
      </View>
    );
  }

  return <View style={mainContainerStyles}>{content}</View>;
};

const mapStateToProps = ({
  machines: { blobAccessInfo, machinesLoaded, ncFileStatistics, machineStatistics, selectedItem },
  time,
  manufacture: { selectedNCFile },
  machinesV2: { selectedChartDate, initialLoading, allowLoadMoreData, loadDataDisabled, displayScrollViewLoader },
}) => ({
  blobAccessInfo,
  time,
  machinesLoaded,
  selectedNCFile,
  ncFileStatistics,
  machineStatistics,
  selectedChartDate,
  initialLoading,
  allowLoadMoreData,
  loadDataDisabled,
  displayScrollViewLoader,
  selectedItem,
});

export default connect(mapStateToProps, {
  switchMachine,
  removeProductionInformation,
  getMachines,
  setSelectedMachine,
  setSelectedNCFile,
  getNCFileStatistics,
  setActionsModalOpen,
  setAllowLoadMoreData,
})(ColorWrapper(MachinesScreenContentWrapperComponent));
